import React, { useState, useMemo, useEffect } from "react";
import dayjs from "dayjs";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import "../../css/barcodeComponent.css";
import "primeflex/primeflex.css";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primeicons/primeicons.css";
import { useSetRecoilState, useRecoilValue, useRecoilState } from "recoil";
import { orderInfoIsViewState } from "../../stores/barcode/orderInfoIsViewState";
import { barcodeDataState } from "../../stores/barcode/barcodeDataState";
import { trackingValueState } from "../../stores/barcode/trackingValueState";
import { createTitle } from "../../utils/createTitle";
import { loginState } from "../../stores/loginState";
import useGetBarcodeData from "../../api/barcode/useGetBarcodeData";
import { useSendInvoice } from "../../api/barcode/useSendInvoice";
import { useGetTracking } from "../../api/barcode/useGetTracking";
import Loading from "../Loading";
import { formatNumberCommas } from "../../utils/formatNumberCommas";
import { downloadExcel } from "./DownloadExcel";
import Swal from "sweetalert2";
import { isBarcodeLoading } from "../../stores/barcode/isBarcodeLoading";
import { kpcScanPaginationState } from "../../stores/barcode/kpcScanPaginationState";
import { isKpcCompany, KPC_WORK_TYPES } from "../../utils/kpc";
import { useKpcScanResend } from "../../api/barcode/useKpcScanResend";
import { useRefreshLotteCarInfo } from "../../api/barcode/useRefreshLotteCarInfo";

export default function BarcodeListsComponent(props) {
  // 상태값 선언
  const login = useRecoilValue(loginState);
  const barcodeData = useRecoilValue(barcodeDataState);
  const setOrderInfoIsView = useSetRecoilState(orderInfoIsViewState);
  const [trackingValue, setTrackingValue] = useRecoilState(trackingValueState);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const today = new Date().toISOString().split("T")[0];
  const [isLoading, setIsLoading] = useRecoilState(isBarcodeLoading);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [workType, setWorkType] = useState("전체");
  const [barcode, setBarcode] = useState("");
  const [resultCode, setResultCode] = useState("전체");
  const normalizedBarcode = barcode.replace(/-/g, "");
  const isKpc = isKpcCompany(login.company);
  const [kpcPage, setKpcPage] = useState(0);
  const [kpcRows, setKpcRows] = useState(50);
  const kpcPagination = useRecoilValue(kpcScanPaginationState);

  // 운송장 조회 API
  const { mutateAsync: getBarcodeData } = useGetBarcodeData({
    startDate,
    endDate,
    workType,
    barcode: normalizedBarcode,
    resultCode,
    page: kpcPage,
    size: kpcRows,
  });

  // 대상 고객에게 송장 전송 API
  const { mutate: sendInvoiceMutate } = useSendInvoice({ startDate, endDate });
  const { mutateAsync: resendKpcScans } = useKpcScanResend();
  const { mutateAsync: refreshLotteCarInfo } = useRefreshLotteCarInfo();

  // 운송장 추적 API
  const { mutate: getTracking } = useGetTracking(trackingValue.barcode);

  // 운송장 데이터 갱신 시 날짜 범위 동기화
  useEffect(() => {
    setOrderInfoIsView(false);

    // KPC는 서버 페이지 단위 응답이므로 현재 페이지 데이터로 조회 날짜를 덮어쓰지 않는다.
    if (isKpc) {
      return;
    }

    if (barcodeData && barcodeData.length > 0) {
      const dates = barcodeData.map((data) => new Date(data.scandate));
      const minDate = new Date(Math.min(...dates));
      const maxDate = new Date(Math.max(...dates));
      setStartDate(minDate.toISOString().split("T")[0]);
      setEndDate(maxDate.toISOString().split("T")[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barcodeData, isKpc]);

  // 테이블 선택 처리
  const onSelectionChange = (e) => {
    setSelectedProduct(e.value);
    // KPC는 화물추적 API 계약 범위가 아니므로 상세 추적 화면을 열지 않는다.
    setOrderInfoIsView(isKpc ? false : !!e.value);
  };

  // 기본 제목 구성
  const title = useMemo(
    () =>
      createTitle([
        { label: "운송장번호", key: "barcode" },
        { label: "스캔일자", key: "scandate" },
        { label: "스캔시간", key: "scantime" },
        { label: "차량번호", key: "car_num" },
        { label: "업무구분", key: "tm_dv" },
        { label: "카메라 ID", key: "scannername" },
        { label: "작업영업소", key: "bran_cd" },
        { label: "작업영업사원", key: "emp_cd" },
        { label: "출발영업소", key: "tg_bran_cd" },
        { label: "출발영업사원", key: "pob" },
        { label: "이미지 존재여부", key: "img_exists" },
        { label: "스캐너", key: "dev_serial" },
      ]),
    [],
  );

  // 로젠 제목 구성
  const logenTitle = useMemo(
    () =>
      createTitle([
        { label: "운송장번호", key: "barcode" },
        { label: "스캔일자", key: "scandate" },
        { label: "스캔시간", key: "scantime" },
        { label: "차량번호", key: "car_num" },
        { label: "업무구분", key: "tm_dv" },
        { label: "카메라 ID", key: "scannername" },
        { label: "작업영업소", key: "bran_cd" },
        { label: "작업영업사원", key: "emp_cd" },
        { label: "출발영업소", key: "tg_bran_cd" },
        { label: "출발영업사원", key: "pob" },
        { label: "이미지 존재여부", key: "img_exists" },
        { label: "스캐너", key: "dev_serial" },
        { label: "결과 코드", key: "result_no" },
        { label: "결과 메시지", key: "result_msg" },
      ]),
    [],
  );

  // 한진 제목 구성
  const hanjinTitle = useMemo(
    () =>
      createTitle([
        { label: "운송장번호", key: "barcode" },
        { label: "스캔일자", key: "scandate" },
        { label: "스캔시간", key: "scantime" },
        { label: "간선명", key: "trk" },
        { label: "업무구분", key: "tm_dv" },
        { label: "카메라 ID", key: "scannername" },
        { label: "작업영업소", key: "bran_cd" },
        { label: "작업영업사원", key: "emp_cd" },
        { label: "출발영업사원", key: "pob" },
      ]),
    [],
  );

  // 한덱스 제목 구성
  const handexTitle = useMemo(
    () =>
      createTitle([
        { label: "운송장번호", key: "barcode" },
        { label: "스캔일자", key: "scandate" },
        { label: "스캔시간", key: "scantime" },
        { label: "업무구분", key: "tm_dv" },
        { label: "카메라 ID", key: "scannername" },
        { label: "작업영업소", key: "bran_cd" },
        { label: "작업영업사원", key: "emp_cd" },
        { label: "출발영업사원", key: "pob" },
      ]),
    [],
  );

  // KPC는 스캔 원장의 신규 표준 필드를 기존 택배사와 구분해 그대로 표시한다.
  const kpcTitle = useMemo(
    () =>
      createTitle([
        { label: "운송장번호", key: "barcode" },
        { label: "스캔일자", key: "scandate" },
        { label: "스캔시간", key: "scantime" },
        { label: "업무구분", key: "tm_dv" },
        { label: "스캐너명", key: "scanner_nm" },
        { label: "CCTV 카메라 ID", key: "camera_id" },
        { label: "전송상태", key: "send_status" },
        { label: "결과 코드", key: "result_no" },
        { label: "결과 메시지", key: "result_msg" },
      ]),
    [],
  );

  // 회사별 제목 선택
  const selectedTitle = useMemo(() => {
    const company = login.company?.toUpperCase();
    if (company === "HANJIN") {
      return hanjinTitle;
    }
    if (company === "HANDEX") {
      return handexTitle;
    }
    if (company === "LOGEN") {
      return logenTitle;
    }
    if (company === "KPC") {
      return kpcTitle;
    }
    return title;
  }, [login.company, title, logenTitle, hanjinTitle, handexTitle, kpcTitle]);

  // 고객사별 자동 전송 텍스트
  const companyAutoComment =
    login.company?.toUpperCase() === "LOGEN"
      ? "iLogen"
      : login.company?.toUpperCase() === "LOTTE"
        ? "롯데"
        : "";

  // 송장 전송
  const onClickSendInvoice = () => {
    sendInvoiceMutate();
  };

  // 롯데 연계일보 정보를 서버에서 최신 상태로 갱신한다.
  const onClickRefreshLotteCarInfo = async () => {
    setIsLoading(true);
    try {
      const response = await refreshLotteCarInfo();
      if (response?.result !== "00") {
        throw new Error(response?.message || "연계일보 갱신에 실패했습니다.");
      }

      await Swal.fire({
        icon: "success",
        title: "연계일보 갱신 완료",
        text: response.message || "연계일보 정보가 갱신되었습니다.",
        confirmButtonText: "확인",
      });
    } catch (error) {
      console.error("롯데 연계일보 갱신 실패", error);
      Swal.fire({
        icon: "error",
        title: "연계일보 갱신에 실패했습니다.",
        text: error.message || "잠시 후 다시 시도해 주세요.",
        confirmButtonText: "확인",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // KPC 재전송 API는 단일 운행일자만 허용한다.
  const onClickKpcResend = async () => {
    if (startDate !== endDate) {
      Swal.fire({
        icon: "warning",
        title: "재전송할 날짜를 하루로 선택해 주세요.",
        text: "KPC 영일데이타 재전송은 단일 운행일자 기준으로 처리됩니다.",
        confirmButtonText: "확인",
      });
      return;
    }

    const confirmation = await Swal.fire({
      icon: "question",
      title: "영일데이타 재전송",
      text: `${startDate}의 미완료 전송 건을 다시 전송하시겠습니까?`,
      showCancelButton: true,
      confirmButtonText: "재전송",
      cancelButtonText: "취소",
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await resendKpcScans(startDate);
      if (response.result !== "00") {
        throw new Error(response.message || "영일데이타 재전송에 실패했습니다.");
      }

      const result = response.data || {};
      await Swal.fire({
        icon: Number(result.failureCount || 0) > 0 ? "warning" : "success",
        title: "영일데이타 재전송 완료",
        html: `전체 ${formatNumberCommas(result.totalCount || 0)}건<br/>성공 ${formatNumberCommas(result.successCount || 0)}건<br/>중복 ${formatNumberCommas(result.duplicateCount || 0)}건<br/>실패 ${formatNumberCommas(result.failureCount || 0)}건`,
        confirmButtonText: "확인",
      });
      await getBarcodeData({ page: kpcPage, size: kpcRows });
    } catch (error) {
      console.error("KPC 영일데이타 재전송 실패", error);
      Swal.fire({
        icon: "error",
        title: "영일데이타 재전송에 실패했습니다.",
        text: error.message || "잠시 후 다시 시도해 주세요.",
        confirmButtonText: "확인",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // 조회 실행
  const onClickSearch = async () => {
    if (startDate <= endDate) {
      if (isKpc && dayjs(endDate).diff(dayjs(startDate), "day") > 30) {
        Swal.fire({
          icon: "warning",
          title: "KPC 조회 기간은 최대 31일입니다.",
          confirmButtonText: "확인",
        });
        return;
      }

      setIsLoading(true);
      try {
        if (isKpc) {
          setKpcPage(0);
          await getBarcodeData({ page: 0, size: kpcRows });
        } else {
          await getBarcodeData();
        }
      } catch (error) {
        console.error("운송장 데이터 조회 중 오류가 발생했습니다.", error);
      } finally {
        setIsLoading(false);
      }
    } else {
      Swal.fire({
        icon: "warning",
        title: "조회일자가 올바르지 않습니다.",
        confirmButtonText: "확인",
      });
    }
    setOrderInfoIsView(false);
  };

  // 업무구분 표시 변환
  const workTypeTmdv = (rowData) => {
    if (rowData.tm_dv === KPC_WORK_TYPES.GENERAL_INBOUND) {
      return "일반입고(700131)";
    }
    if (rowData.tm_dv === KPC_WORK_TYPES.SLX_PICKUP) {
      return "SLX집하(700112)";
    }
    if (rowData.tm_dv === "60") {
      return "배송입고(60)";
    }
    if (rowData.tm_dv === "30") {
      return "집하출고(30)";
    }
    if (rowData.tm_dv === "21") {
      return "도착(21)";
    }
    if (rowData.tm_dv === "20") {
      return "발송(20)";
    }
    if (rowData.tm_dv === "31") {
      return "간선도착(31)";
    }
    if (rowData.tm_dv === "32") {
      return "간선출발(32)";
    }
    if (rowData.tm_dv === "14") {
      return "배송완료(14)";
    }
    if (rowData.tm_dv === "DD") {
      return "배송인수확인(DD)";
    }
    return rowData.tm_dv;
  };

  // row_num 포맷
  const workTypeRownum = (rowData) => {
    if (rowData.row_num) {
      return formatNumberCommas(rowData.row_num);
    }
    return rowData.row_num;
  };

  // 이미지 존재 여부
  const workTypeImg = (rowData) => {
    return rowData.img_exists ? "O" : "X";
  };

  // 스캐너 구분 표시
  const workTypeSerial = (rowData) => {
    if (rowData.dev_serial === "1541084") {
      return "오토스캐너";
    }
    if (rowData.dev_serial !== "1541084") {
      return "PDA";
    }
    return "기타";
  };

  // 행 선택 시 추적 조회
  const handleRowClick = async (rowData) => {
    // KPC 문서에는 화물추적 API가 없으므로 원장 행 선택 시 별도 추적 요청을 하지 않는다.
    if (isKpc) {
      return;
    }

    setTrackingValue({
      barcode: rowData.barcode,
      scanDate: rowData.scandate,
    });
    setTimeout(() => {
      getTracking();
    }, 0);
  };

  // KPC 스캔 원장은 서버 페이지네이션을 사용한다.
  const onKpcPageChange = async (event) => {
    setKpcPage(event.page);
    setKpcRows(event.rows);
    setIsLoading(true);

    try {
      await getBarcodeData({ page: event.page, size: event.rows });
    } catch (error) {
      console.error("KPC 스캔 원장 페이지를 조회하는 중 오류가 발생했습니다.", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Excel 다운로드
  const onClickExcelDownload = () => {
    downloadExcel(
      barcodeData,
      `${startDate === endDate ? startDate : `${startDate}~${endDate}`} 운송장데이터조회.xlsx`,
    );
  };

  // Enter 검색
  const handleKeyDownEnter = (e) => {
    if (e.key === "Enter") {
      onClickSearch();
    }
  };

  return (
    <div
      onKeyDown={handleKeyDownEnter}
      className="card"
      style={{
        width: "100%",
        maxWidth: "1200px",
        minHeight: "800px",
        height: "calc(100vh - 40px)",
        border: `1px solid ${props.theme.white4}`,
        background: props.theme.white1,
        color: props.theme.black1,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {isLoading && <Loading />}
      <span
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          padding: 0,
        }}
      >
        <h3 style={{ position: "relative", left: "0", margin: "0" }}>
          운송장관리
        </h3>
        <span style={{ display: "flex", padding: 0 }}>
          <div
            style={{
              textAlign: "right",
              marginLeft: "30px",
              color: props.theme.black1,
              fontSize: "16px",
            }}
          >
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              {login.company?.toUpperCase() === "LOGEN"
                ? "배송입고 (60) "
                : login.company?.toUpperCase() === "LOTTE"
                  ? "도착 (21) "
                  : login.company?.toUpperCase() === "KPC"
                    ? "일반입고 (700131) "
                  : "간선출발 (32) "}
              :{" "}
              <p style={{ width: "100px" }}>
                {isKpc
                  ? formatNumberCommas(
                      kpcPagination.summary?.generalInboundCount || 0,
                    )
                  : barcodeData
                    ? formatNumberCommas(
                        barcodeData.filter(
                          (item) =>
                            item.tm_dv === "60" ||
                            item.tm_dv === "21" ||
                            item.tm_dv === "32" ||
                            item.tm_dv === "15",
                        ).length,
                      )
                    : 0}{" "}
                건
              </p>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              {login.company?.toUpperCase() === "LOGEN"
                ? "집하출고 (30) "
                : login.company?.toUpperCase() === "LOTTE"
                  ? "발송 (20) "
                  : login.company?.toUpperCase() === "KPC"
                    ? "SLX집하 (700112) "
                  : "간선도착 (31) "}
              :{" "}
              <p style={{ width: "100px" }}>
                {isKpc
                  ? formatNumberCommas(
                      kpcPagination.summary?.slxPickupCount || 0,
                    )
                  : barcodeData
                    ? formatNumberCommas(
                        barcodeData.filter(
                          (item) =>
                            item.tm_dv === "30" ||
                            item.tm_dv === "20" ||
                            item.tm_dv === "31" ||
                            item.tm_dv === "50",
                        ).length,
                      )
                    : 0}{" "}
                건
              </p>
            </div>
          </div>

          {(login.company?.toUpperCase() === "HANJIN" ||
            login.company?.toUpperCase() === "HANDEX") && (
            <div
              style={{
                textAlign: "right",
                marginLeft: "30px",
                color: props.theme.black1,
                fontSize: "16px",
              }}
            >
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                {"배송완료 (14) "}:{" "}
                <p style={{ width: "100px" }}>
                  {barcodeData
                    ? formatNumberCommas(
                        barcodeData.filter((item) => item.tm_dv === "14")
                          .length,
                      )
                    : 0}{" "}
                  건
                </p>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                {"배송인수확인 (DD) "}:{" "}
                <p style={{ width: "100px" }}>
                  {barcodeData
                    ? formatNumberCommas(
                        barcodeData.filter((item) => item.tm_dv === "DD")
                          .length,
                      )
                    : 0}{" "}
                  건
                </p>
              </div>
            </div>
          )}
          {login.company?.toUpperCase() === "LOTTE" && (
            <button
              style={{
                padding: "5px 15px",
                border: `1px solid ${props.theme.white1}`,
                background: props.theme.colorwhite1,
                color: props.theme.black1,
                borderRadius: "10px",
                marginLeft: "50px",
              }}
              onClick={onClickRefreshLotteCarInfo}
            >
              연계일보 갱신
            </button>
          )}
          {(login.company?.toUpperCase() === "LOGEN" ||
            login.company?.toUpperCase() === "LOTTE") && (
            <button
              style={{
                padding: "5px 15px",
                border: `1px solid ${props.theme.white1}`,
                background: props.theme.colorwhite1,
                color: props.theme.black1,
                borderRadius: "10px",
                marginLeft:
                  login.company?.toUpperCase() === "LOTTE" ? "20px" : "50px",
              }}
              onClick={onClickSendInvoice}
            >
              {companyAutoComment} 자동전송
            </button>
          )}
          {isKpc && (
            <button
              style={{
                padding: "5px 15px",
                border: `1px solid ${props.theme.white1}`,
                background: props.theme.colorwhite1,
                color: props.theme.black1,
                borderRadius: "10px",
                marginLeft: "50px",
              }}
              onClick={onClickKpcResend}
            >
              영일데이타 재전송
            </button>
          )}
          <button
            style={{
              padding: "5px 15px",
              border: `1px solid ${props.theme.white1}`,
              background: props.theme.colorwhite1,
              color: props.theme.black1,
              borderRadius: "10px",
              marginLeft: "20px",
            }}
            onClick={onClickExcelDownload}
          >
            Excel 다운로드
          </button>
        </span>
      </span>
      <span
        className="filter"
        style={{
          border: `1px solid ${props.theme.white1}`,
          background: props.theme.white2,
          color: props.theme.black1,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          padding: "10px",
          height: "auto",
          gap: "10px 20px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            whiteSpace: "nowrap",
          }}
        >
          <p style={{ margin: 0 }}>조회일자</p>
          <input
            type="date"
            className="date"
            style={{ background: props.theme.white0 }}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          -
          <input
            type="date"
            className="date"
            style={{ background: props.theme.white0 }}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        {/* KPC는 별도 전산을 보조하는 원장 조회 화면이므로 업무구분 필터 없이 날짜·바코드만 제공한다. */}
        {!isKpc && (
          <>
            <div
              style={{
                color: props.theme.white4,
                display: window.innerWidth > 1200 ? "block" : "none",
              }}
            >
              |
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                whiteSpace: "nowrap",
              }}
            >
              <p style={{ margin: 0 }}>업무구분</p>
              <select
                className="date"
                value={workType}
                onChange={(e) => setWorkType(e.target.value)}
                style={{ background: props.theme.white0, width: "120px" }}
              >
                <option value="전체">전체</option>
                <option
                  value={
                    login.company?.toUpperCase() === "LOGEN"
                      ? "60"
                      : login.company?.toUpperCase() === "LOTTE"
                        ? "21"
                        : login.company?.toUpperCase() === "HANJIN"
                          ? "31"
                          : "15"
                  }
                >
                  {login.company?.toUpperCase() === "LOGEN"
                    ? "배송입고 (60)"
                    : login.company?.toUpperCase() === "LOTTE"
                      ? "도착 (21)"
                      : "간선출발 (32)"}
                </option>
                <option
                  value={
                    login.company?.toUpperCase() === "LOGEN"
                      ? "30"
                      : login.company?.toUpperCase() === "LOTTE"
                        ? "20"
                        : login.company?.toUpperCase() === "HANJIN"
                          ? "32"
                          : "50"
                  }
                >
                  {login.company?.toUpperCase() === "LOGEN"
                    ? "집하출고 (30)"
                    : login.company?.toUpperCase() === "LOTTE"
                      ? "발송 (20)"
                      : "간선도착 (31)"}
                </option>
              </select>
            </div>
          </>
        )}

        {login.company?.toUpperCase() === "LOGEN" && (
          <>
            <div
              style={{
                color: props.theme.white4,
                display: window.innerWidth > 1200 ? "block" : "none",
              }}
            >
              |
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                whiteSpace: "nowrap",
              }}
            >
              <p style={{ margin: 0 }}>결과 코드</p>
              <select
                className="date"
                value={resultCode}
                onChange={(e) => setResultCode(e.target.value)}
                style={{ background: props.theme.white0, width: "100px" }}
              >
                <option value="전체">전체</option>
                <option value="99">성공 (99)</option>
                <option value="0">실패 (0)</option>
              </select>
            </div>
          </>
        )}

        <div
          style={{
            color: props.theme.white4,
            display: window.innerWidth > 1200 ? "block" : "none",
          }}
        >
          |
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            whiteSpace: "nowrap",
          }}
        >
          <p style={{ margin: 0 }}>운송장 번호</p>
          <input
            placeholder="번호를 입력해주세요."
            className="date"
            value={barcode}
            onChange={(e) => setBarcode(e.target.value)}
            style={{ background: props.theme.white0, width: "160px" }}
          />
          <button
            className="date"
            id="button"
            style={{ background: props.theme.white3, width: "70px" }}
            onClick={onClickSearch}
          >
            검색
          </button>
        </div>
      </span>
      {/* 고객사별 행 구조와 페이지 처리 방식이 달라 전환 시 내부 상태를 새로 만든다. */}
      <DataTable
        key={login.company || "UNKNOWN"}
        value={barcodeData}
        rows={isKpc ? kpcRows : 25}
        rowsPerPageOptions={[5, 10, 25, 50]}
        {...(isKpc
          ? {
              lazy: true,
              first: kpcPage * kpcRows,
              totalRecords: kpcPagination.totalCount,
              onPage: onKpcPageChange,
            }
          : {})}
        tableStyle={{
          minWidth:
            login.company?.toUpperCase() === "LOGEN" || isKpc ? "1500px" : "100%",
        }}
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        paginatorLeft={
          <div
            style={{
              position: "absolute",
              left: 0,
              color: props.theme.black1,
              fontSize: "18px",
              bottom: "15px",
            }}
          >
            총 검색 결과:{" "}
            {isKpc
              ? formatNumberCommas(kpcPagination.totalCount)
              : barcodeData
                ? formatNumberCommas(barcodeData.length)
                : 0} 건
          </div>
        }
        paginatorRight={<></>}
        selectionMode="single"
        selection={selectedProduct}
        onSelectionChange={onSelectionChange}
        dataKey={isKpc ? "row_key" : "id"}
        emptyMessage=" "
        paginator
        paginatorClassName="Indigo 200"
        scrollable
        scrollHeight="calc(100vh - 350px)"
      >
        {selectedTitle.map((col, index) => (
          <Column
            key={col.key || index}
            field={col.key}
            header={col.label}
            sortable={false}
            body={(rowData) => (
              <div
                onClick={() => handleRowClick(rowData)}
                title={rowData[col.key] ?? ""}
                style={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}
              >
                {col.key === "row_num"
                  ? workTypeRownum(rowData)
                  : col.key === "tm_dv"
                    ? workTypeTmdv(rowData)
                    : col.key === "img_exists"
                      ? workTypeImg(rowData)
                      : col.key === "dev_serial"
                        ? workTypeSerial(rowData)
                        : (rowData[col.key] ?? "")}
              </div>
            )}
            headerStyle={{
              backgroundColor: props.theme.white2,
              color: props.theme.black1,
              border: `1px solid ${props.theme.white4}`,
              zIndex: 1,
            }}
            style={{
              fontSize: "13px",
              width: col.key === "result_msg" ? "250px" : "max-content",
              minWidth: "120px",
              height: "10px",
              padding: "5px 10px",
              border: `1px solid ${props.theme.white4}`,
              backgroundColor: "unset",
              color: props.theme.black1,
              textAlign: "center",
              cursor: "pointer",
            }}
          />
        ))}
      </DataTable>
    </div>
  );
}
